import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Save, Eye, X } from 'lucide-react';
import { TripBasicInfoForm } from '@/components/trip/BasicInfoForm';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { tripService } from '@/lib/supabase/trips';

// Função para gerar ID único
const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

interface Client {
  id: string;
  name: string;
  email?: string;
  phone: string;
  notes: string;
}

interface Trip {
  id: string;
  title?: string;
  clientIds: string[];
  destination: string[] | string;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  status: string;
  notes?: string;
  days?: any[];
  coverImages?: string[];
  client?: string | string[];
}

const extractDate = (dateVal: any) => {
  if (!dateVal) return '';
  if (typeof dateVal === 'string') {
    // Se for string ISO, extrai só a data
    return dateVal.split('T')[0];
  }
  if (dateVal instanceof Date) {
    return dateVal.toISOString().slice(0, 10);
  }
  return '';
};

export default function TripForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(!!id);
  const [trip, setTrip] = useState<Trip>({
    id: id || generateId(),
    title: '',
    clientIds: [],
    destination: '',
    startDate: '',
    endDate: '',
    status: 'quotation',
    notes: '',
    days: [],
    coverImages: [],
    client: [],
  });
  const [clientSearch, setClientSearch] = useState('');
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [selectedClients, setSelectedClients] = useState<Client[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const [leaveTo, setLeaveTo] = useState("");

  useEffect(() => {
    const loadTrip = async () => {
      if (id) {
        // 1. Tenta buscar do Supabase
        try {
          const supabaseTrip = await tripService.getTripById(id);
          if (supabaseTrip) {
            setTrip({
              id: supabaseTrip.id,
              title: supabaseTrip.title,
              clientIds: supabaseTrip.client_ids || [],
              destination: Array.isArray(supabaseTrip.destination) ? supabaseTrip.destination.join(', ') : supabaseTrip.destination || '',
              startDate: extractDate(supabaseTrip.start_date),
              endDate: extractDate(supabaseTrip.end_date),
              status: (supabaseTrip.status as Trip['status']) || 'quotation',
              notes: supabaseTrip.notes || '',
              days: supabaseTrip.days || [],
            });
            return;
          }
        } catch (err) {
          // Se erro, tenta localStorage
        }
        // 2. Se não achou no Supabase, tenta localStorage
        const savedTrips = localStorage.getItem('voah_trips');
        if (savedTrips) {
          const trips = JSON.parse(savedTrips);
          const existingTrip = trips.find((t: Trip) => String(t.id) === String(id));
          if (existingTrip) {
            setTrip({
              ...existingTrip,
              startDate: extractDate(existingTrip.startDate),
              endDate: extractDate(existingTrip.endDate),
              status: existingTrip.status || 'quotation',
              days: existingTrip.days || [],
            });
            return;
          }
        }
        // 3. Se não achou, mostra toast de erro
        toast({
          title: 'Viagem não encontrada',
          description: 'Não foi possível localizar a viagem para edição.',
          variant: 'destructive',
        });
      }
    };
    loadTrip();
  }, [id, toast]);

  useEffect(() => {
    const loadClients = () => {
      const savedClients = localStorage.getItem('voah_clients');
      if (savedClients) {
        const clients = JSON.parse(savedClients);
        setAvailableClients(clients);
        if (trip.clientIds?.length > 0) {
          const selected = clients.filter((client: Client) => trip.clientIds.includes(client.id));
          setSelectedClients(selected);
        }
      }
    };
    loadClients();
  }, [trip.clientIds]);

  const handleChange = (field: keyof Trip, value: any) => {
    // Atualizar o estado local
    const updated = {
      ...trip,
      [field]: value
    };
    
    setTrip(updated);
    
    // Chamar a função onTripChange se existir
    // if (onTripChange) {
    //   onTripChange(updated);
    // }
  };

  const handleSave = async (e?: React.FormEvent<Element>) => {
    if (e) e.preventDefault();
    try {
      // Validação básica
      if (!trip.title || !trip.destination || trip.clientIds.length === 0) {
        toast({
          title: 'Campos obrigatórios',
          description: 'Preencha título, destino e pelo menos um cliente.',
          variant: 'destructive',
        });
        return false;
      }
      let savedTrip;
      const destinationArr = Array.isArray(trip.destination)
        ? trip.destination
        : trip.destination.split(',').map((d) => d.trim()).filter(Boolean);
      const status = (['quotation', 'itinerary', 'day-by-day'].includes(trip.status)
        ? trip.status
        : 'quotation') as 'quotation' | 'itinerary' | 'day-by-day';
      const startDate = typeof trip.startDate === 'string' ? trip.startDate : (trip.startDate ? trip.startDate.toString() : null);
      const endDate = typeof trip.endDate === 'string' ? trip.endDate : (trip.endDate ? trip.endDate.toString() : null);
      if (isEditing) {
        // Atualizar viagem existente
        savedTrip = await tripService.updateTrip(trip.id, {
          title: trip.title || '',
          client_ids: trip.clientIds,
          destination: destinationArr,
          start_date: startDate,
          end_date: endDate,
          status,
          notes: trip.notes || '',
          days: trip.days || [],
        });
      } else {
        // Criar nova viagem
        savedTrip = await tripService.createTrip({
          title: trip.title || '',
          client_ids: trip.clientIds,
          destination: destinationArr,
          start_date: startDate,
          end_date: endDate,
          status,
          notes: trip.notes || '',
          days: trip.days || [],
        });
      }
      toast({
        title: 'Viagem salva',
        description: 'A viagem foi salva com sucesso.',
        variant: 'default',
      });
      navigate('/admin/travel-hub');
      return true;
    } catch (error) {
      toast({
        title: 'Erro ao salvar viagem',
        description: 'Ocorreu um erro ao salvar. Tente novamente.',
        variant: 'destructive',
      });
      return false;
    }
  };

  const handleClientSearch = (value: string) => {
    setClientSearch(value);
    if (!value.trim()) {
      setAvailableClients([]);
      return;
    }
    const savedClients = localStorage.getItem('voah_clients');
    if (savedClients) {
      const clients = JSON.parse(savedClients);
      const filtered = clients.filter((client: Client) => 
        client.name.toLowerCase().includes(value.toLowerCase()) &&
        !(trip.clientIds?.includes(client.id))
      );
      setAvailableClients(filtered);
    }
  };

  const handleAddClient = (client: Client) => {
    if (!trip.clientIds?.includes(client.id)) {
      handleChange('clientIds', [...(trip.clientIds || []), client.id]);
      setSelectedClients(prev => [...prev, client]);
      setClientSearch('');
      setAvailableClients([]);
    }
  };

  const handleRemoveClient = (clientId: string) => {
    handleChange('clientIds', (trip.clientIds || []).filter(id => id !== clientId));
    setSelectedClients(prev => prev.filter(client => client.id !== clientId));
  };

  const confirmNavigation = (to: string) => {
    if (isDirty) {
      setLeaveTo(to);
      setIsLeaveDialogOpen(true);
    } else {
      navigate('/admin/travel-hub');
    }
  };
  
  const handleConfirmLeave = () => {
    setIsLeaveDialogOpen(false);
    navigate(leaveTo);
  };

  const getTitle = () => {
    if (isEditing) {
      return `Editar Viagem: ${trip.destination || ''}`;
    }
    return "Nova Viagem";
  };

  const handlePreview = () => {
    // Implementação futura
  };

  return (
    <AdminLayout
      title={getTitle()}
      onBack={() => confirmNavigation('/admin/trips')}
      showActions={true}
      onSubmit={handleSave}
      onPreview={handlePreview}
    >
      <div className="max-w-5xl mx-auto space-y-8">
        <TripBasicInfoForm 
          trip={trip} 
          handleChange={handleChange}
          isEditing={isEditing}
        />
            
        <div className="flex justify-end space-x-2 mt-8">
          <Button
            variant="outline"
            onClick={() => confirmNavigation('/admin/trips')}
          >
            Cancelar
          </Button>
          
          <Button
            onClick={handleSave}
            className="bg-voah-teal hover:bg-voah-teal-dark"
          >
            <Save className="mr-2 h-4 w-4" />
            Salvar Viagem
          </Button>
        </div>
      </div>
      
      <AlertDialog open={isLeaveDialogOpen} onOpenChange={setIsLeaveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deseja sair sem salvar?</AlertDialogTitle>
            <AlertDialogDescription>
              Você tem alterações não salvas. Se sair agora, essas alterações serão perdidas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmLeave}>
              Sair sem salvar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
} 